// import firebase from "firebase/app";
import "firebase/firestore";
import { db, isFirebaseInitialized, ensureFirebaseInitialized } from "./config";

// Collections
const PRODUCTS_COLLECTION = "products";
const FOUNDER_VIDEOS_COLLECTION = "founderVideos";
const ORDERS_COLLECTION = "orders";
const CONTACT_MESSAGES_COLLECTION = "contactMessages";
const CARTS_COLLECTION = "carts";
const WISHLISTS_COLLECTION = "wishlists";
// const ANNOUNCEMENTS_COLLECTION = "announcements";
const COUPONS_COLLECTION = "coupons";
const NEWSLETTER_SIGNUPS_COLLECTION = "newsletterSignups";

// Helper function to check if product exists
const productExists = async (productId) => {
  try {
    // Ensure Firebase is initialized
    if (!db) {
      const { ensureFirebaseInitialized } = await import('./config');
      await ensureFirebaseInitialized();
      if (!db) {
        console.warn('⚠️ Database not available for productExists check');
        return false;
      }
    }

    const productRef = db.collection(PRODUCTS_COLLECTION).doc(productId);
    const productSnap = await productRef.get();
    return productSnap.exists;
  } catch (error) {
    console.error('Error checking if product exists:', error);
    return false;
  }
};

// Rate limiting (available for other operations if needed)
let requestCount = 0;
let lastRequestTime = 0;
const REQUEST_LIMIT = 10;
const REQUEST_WINDOW = 60000;

// eslint-disable-next-line no-unused-vars
const checkRateLimit = () => {
  const now = Date.now();

  if (now - lastRequestTime > REQUEST_WINDOW) {
    requestCount = 0;
    lastRequestTime = now;
  }

  if (requestCount >= REQUEST_LIMIT) {
    return false;
  }

  requestCount++;
  return true;
};

// Helper function to calculate data size in bytes
const calculateDataSize = (data) => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch (error) {
    // Fallback: approximate size
    return JSON.stringify(data).length * 2; // Rough estimate for UTF-16
  }
};

// Helper function to compress product data if it exceeds 1MB
const compressProductData = (productData) => {
  const MAX_SIZE = 1024 * 1024; // 1MB in bytes
  const dataSize = calculateDataSize(productData);

  if (dataSize <= MAX_SIZE) {
    return productData; // No compression needed
  }

  // Create a copy to modify
  const compressedData = { ...productData };
  let currentSize = dataSize;

  // CRITICAL FIX: Do NOT remove base64Data immediately
  // base64Data is essential for cart/wishlist images
  // Only remove it as absolute last resort after trying other optimizations

  // Step 1: Try truncating description if too long
  if (compressedData.description && compressedData.description.length > 10000) {
    compressedData.description = compressedData.description.substring(0, 10000) + '...';
    currentSize = calculateDataSize(compressedData);
  }

  // Step 2: Optimize carousel images - only remove base64 if imageUrl exists AND base64 is very large
  if (currentSize > MAX_SIZE && compressedData.images && Array.isArray(compressedData.images)) {
    compressedData.images = compressedData.images.map((img) => {
      // Only remove base64Data if we have imageUrl AND base64Data is very large (>100KB)
      if (img && typeof img === 'object' && img.base64Data && img.imageUrl && img.base64Data.length > 100000) {
        // Check if imageUrl is a valid permanent URL (not blob)
        if (!img.imageUrl.startsWith('blob:') &&
          (img.imageUrl.startsWith('http') || img.imageUrl.startsWith('data:'))) {
          const { base64Data, ...imgWithoutBase64 } = img;
          return imgWithoutBase64;
        }
      }
      return img;
    });
    currentSize = calculateDataSize(compressedData);
  }

  // Step 3: LAST RESORT - Only remove primary base64Data if document is STILL too large
  // AND we have a valid permanent imageUrl
  if (currentSize > MAX_SIZE && compressedData.base64Data && compressedData.imageUrl) {
    // Only remove if imageUrl is a valid permanent URL (not blob or relative path)
    if (!compressedData.imageUrl.startsWith('blob:') &&
      (compressedData.imageUrl.startsWith('http') || compressedData.imageUrl.startsWith('data:'))) {
      delete compressedData.base64Data;
      currentSize = calculateDataSize(compressedData);
    }
  }

  // If still too large, throw error with helpful message
  if (currentSize > MAX_SIZE) {
    const sizeMB = (currentSize / (1024 * 1024)).toFixed(2);
    throw new Error(
      `Product data is too large (${sizeMB}MB). Maximum size is 1MB. ` +
      `Please reduce the number of images or use smaller image files. ` +
      `Consider storing images in Firebase Storage instead of base64.`
    );
  }

  return compressedData;
};

// Products operations
export const getProducts = async (retryCount = 0, forceFresh = false) => {
  // Always use Firestore - no localStorage fallback
  const maxRetries = 3; // Increased from 2 for better reliability
  // CRITICAL: Increased timeout for admin operations and slow networks
  const baseTimeout = 20000; // Increased from 8s to 20s for better reliability
  const retryDelay = 2000; // Increased from 1000ms for better retry spacing

  console.log('🔍 getProducts called:', {
    retryCount,
    forceFresh,
    hasDb: !!db,
    dbType: db ? typeof db : 'null'
  });

  // Check if Firebase is initialized - wait a bit if not ready
  if (!db) {
    console.warn('⚠️ Firestore database not initialized, waiting...');
    // Try to ensure Firebase is initialized
    try {
      const { ensureFirebaseInitialized } = await import('./config');
      await ensureFirebaseInitialized();
    } catch (initError) {
      console.error('❌ Failed to initialize Firebase:', initError);
    }

    // Wait up to 2000ms for Firebase to initialize (increased from 1000ms)
    for (let i = 0; i < 20; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      if (db) {
        console.log('✅ Firebase db initialized after waiting');
        break;
      }
    }

    if (!db) {
      console.error('❌ Firestore database not initialized after waiting');
      console.error('Firebase state:', {
        hasDb: !!db,
        dbType: db ? typeof db : 'null',
        isInitialized: isFirebaseInitialized()
      });
      throw new Error('Firestore database not initialized. Please check Firebase configuration.');
    }
  }

  try {
    console.log('📡 Fetching products from Firestore...');

    // CRITICAL: Check if db.collection is available
    if (typeof db.collection !== 'function') {
      throw new Error('Firestore collection method not available. Database may not be properly initialized.');
    }

    // Don't call enableNetwork() here - it causes "Unexpected state" errors
    // Firestore automatically manages network state
    // Only call enableNetwork() if you've explicitly disabled it first

    const productsRef = db.collection(PRODUCTS_COLLECTION);

    // CRITICAL FIX: Force server fetch if forceFresh is true (for admin dashboard)
    // This prevents showing cached "0 products" when products actually exist
    const getOptions = forceFresh ? { source: 'server' } : {};

    // Use default source - Firestore uses cache first (fast), then server (fresh)
    // This is much faster than forcing server-only reads
    // CRITICAL: Increased timeout for slow networks and large collections
    const timeout = baseTimeout + (retryCount * 5000); // Increase timeout with each retry
    const snapshotPromise = getOptions.source ?
      productsRef.get(getOptions) :
      productsRef.get();

    console.log(`⏱️ Using timeout of ${timeout}ms (attempt ${retryCount + 1})${forceFresh ? ' [FORCING SERVER FETCH]' : ''}`);

    // Use Promise.race with proper cleanup
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`Firestore request timeout after ${timeout}ms`));
      }, timeout);
    });

    try {
      const snapshot = await Promise.race([snapshotPromise, timeoutPromise]);
      clearTimeout(timeoutId);

      console.log('📦 Products snapshot received:', {
        docCount: snapshot.docs.length,
        empty: snapshot.empty,
        size: snapshot.size,
        fromCache: snapshot.metadata?.fromCache || false
      });

      const products = snapshot.docs.map((doc) => {
        const data = doc.data();
        const { id: customId, ...dataWithoutId } = data;
        const product = {
          id: doc.id,
          ...dataWithoutId,
          customId: customId || null
        };

        return product;
      });

      console.log(`✅ Successfully loaded ${products.length} products from Firestore`);
      if (products.length === 0) {
        console.warn('⚠️ WARNING: No products found in Firestore. Check if products collection exists and has data.');
      }
      return products;
    } catch (raceError) {
      clearTimeout(timeoutId);
      throw raceError;
    }
  } catch (error) {
    console.error(`❌ Error getting products (attempt ${retryCount + 1}):`, {
      message: error.message,
      code: error.code,
      stack: error.stack,
      retryCount,
      maxRetries,
      hasDb: !!db,
      dbType: db ? typeof db : 'null'
    });

    // Check if it's an offline error
    const isOfflineError = error.code === 'unavailable' ||
      error.message.includes('offline') ||
      error.message.includes('Failed to get document because the client is offline');

    // Check if it's a timeout or network error
    const isNetworkError = error.message.includes('timeout') ||
      error.message.includes('network') ||
      error.message.includes('unavailable') ||
      isOfflineError;

    // Check if it's a permission error
    if (error.code === 'permission-denied') {
      console.error('❌ PERMISSION DENIED: Check Firestore security rules. Products collection may not be readable.');
      throw new Error('Permission denied. Please check Firestore security rules.');
    }

    if (retryCount < maxRetries && isNetworkError) {
      // Don't call enableNetwork() here - it causes "Unexpected state" errors
      // Firestore will automatically retry when network is available

      // Exponential backoff with longer delays for timeouts
      const delay = retryDelay * Math.pow(2, retryCount); // Use exponential backoff
      console.log(`⏳ Retrying getProducts in ${delay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
      console.log(`⏳ Next attempt will use timeout of ${baseTimeout + ((retryCount + 1) * 5000)}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return getProducts(retryCount + 1, forceFresh);
    }

    // If server fetch failed, try cache/default source as a fallback
    if ((forceFresh || isOfflineError) && db && typeof db.collection === 'function') {
      try {
        console.warn('⚠️ Server fetch failed, retrying with cache/default source...');
        const fallbackSnapshot = await db.collection(PRODUCTS_COLLECTION).get();
        const fallbackProducts = fallbackSnapshot.docs.map((doc) => {
          const data = doc.data();
          const { id: customId, ...dataWithoutId } = data;
          return {
            id: doc.id,
            ...dataWithoutId,
            customId: customId || null
          };
        });
        console.warn(`✅ Loaded ${fallbackProducts.length} products from cache/default source`);
        return fallbackProducts;
      } catch (fallbackError) {
        console.error('❌ Cache/default source fallback failed:', fallbackError);
      }
    }

    // Return empty array instead of localStorage fallback
    console.error('❌ Failed to load products from Firestore after', maxRetries + 1, 'attempts. Returning empty array.');
    if (isOfflineError) {
      console.warn('⚠️ Firestore appears to be offline. Check your internet connection.');
    } else if (error.message.includes('timeout')) {
      console.warn('⚠️ Firestore request timed out. This may indicate:');
      console.warn('   - Slow network connection');
      console.warn('   - Large product collection');
      console.warn('   - Firestore service issues');
      console.warn('   - Check Firestore console for any service alerts');
    }
    return [];
  }
};

export const getProductById = async (productId) => {
  try {
    const productRef = db.collection(PRODUCTS_COLLECTION).doc(productId);
    const snapshot = await productRef.get();
    if (snapshot.exists) {
      return { id: snapshot.id, ...snapshot.data() };
    } else {
      throw new Error("Product not found");
    }
  } catch (error) {
    console.error("Error getting product:", error);
    throw error;
  }
};

export const getProductsByCategory = async (category, retryCount = 0) => {
  // Optimized to fetch only products for specific category - much faster!
  const maxRetries = 2;
  const baseTimeout = 8000; // 8 seconds timeout
  const retryDelay = 1000;

  try {
    if (!db) {
      // Wait briefly for Firebase initialization
      for (let i = 0; i < 5; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (db) break;
      }
      if (!db) {
        throw new Error('Firestore database not initialized');
      }
    }

    const productsRef = db.collection(PRODUCTS_COLLECTION);

    // Normalize category for query (handle variations)
    const normalizedCategory = String(category).toLowerCase().trim();

    // Try exact match first (fastest)
    let q = productsRef.where("category", "==", normalizedCategory);
    let snapshot;

    try {
      // Use default source - Firestore uses cache first (fast), then server
      const timeout = baseTimeout;
      const queryPromise = q.get();

      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Category products request timeout'));
        }, timeout);
      });

      snapshot = await Promise.race([queryPromise, timeoutPromise]);
      clearTimeout(timeoutId);
    } catch (queryError) {
      // If exact match fails, try case-insensitive variations
      // Note: Firestore doesn't support case-insensitive queries directly
      // So we'll need to fetch and filter client-side as fallback
      console.warn('⚠️ Category query failed, trying fallback:', queryError.message);
      throw queryError;
    }

    const products = snapshot.docs.map((doc) => {
      const data = doc.data();
      const { id: customId, ...dataWithoutId } = data;
      return {
        id: doc.id,
        ...dataWithoutId,
        customId: customId || null
      };
    });

    // If no exact matches, try filtering with variations (fallback)
    if (products.length === 0) {
      // Fetch all products and filter client-side (slower but handles variations)
      const allProducts = await getProducts(0, false);
      const filtered = allProducts.filter(product => {
        if (!product.category) return false;
        const productCat = String(product.category).toLowerCase().trim();

        // Exact match
        if (productCat === normalizedCategory) return true;

        // Handle hyphen vs underscore variations
        if (productCat.replace(/-/g, '_') === normalizedCategory.replace(/-/g, '_')) return true;

        // Handle space vs hyphen variations
        if (productCat.replace(/\s+/g, '-') === normalizedCategory ||
          normalizedCategory.replace(/-/g, ' ') === productCat) return true;

        return false;
      });

      if (filtered.length > 0) {
        console.log(`✅ Found ${filtered.length} products for category "${category}" (using fallback filter)`);
        return filtered;
      }
    }

    console.log(`✅ Found ${products.length} products for category "${category}" from Firestore`);
    return products;
  } catch (error) {
    console.error(`Error getting products by category (attempt ${retryCount + 1}):`, error);

    // Retry logic for network errors
    const isNetworkError = error.message.includes('timeout') ||
      error.message.includes('network') ||
      error.message.includes('unavailable');

    if (retryCount < maxRetries && isNetworkError) {
      const delay = retryDelay * Math.pow(1.5, retryCount);
      await new Promise(resolve => setTimeout(resolve, delay));
      return getProductsByCategory(category, retryCount + 1);
    }

    // Return empty array on error
    console.warn(`⚠️ Failed to load products for category "${category}"`);
    return [];
  }
};

export const addProduct = async (productData) => {
  try {
    // Import validation utilities
    const {
      VALIDATION_LIMITS,
      validateProductName,
      validateProductDescription,
      validatePrice,
      validateStock,
      validateTags,
      validateProductSlug,
      generateProductSlug
    } = await import('../utils/validationConstants');

    const { checkProductCreationRateLimit } = await import('../utils/rateLimiter');

    // ===== COMPREHENSIVE VALIDATION =====

    // 1. Required fields validation
    if (!productData.name || !productData.name.trim()) {
      throw new Error("Product name is required");
    }

    if (!productData.category || !productData.category.trim()) {
      throw new Error("Product category is required");
    }

    // At least one price must be provided
    const hasOriginalPrice = productData.originalPrice != null && productData.originalPrice > 0;
    const hasDiscountPrice = productData.discountPrice != null && productData.discountPrice > 0;
    const hasPrice = productData.price != null && productData.price > 0;

    if (!hasOriginalPrice && !hasDiscountPrice && !hasPrice) {
      throw new Error("At least one price (originalPrice, discountPrice, or price) is required");
    }

    // 2. Validate product name
    const nameValidation = validateProductName(productData.name);
    if (!nameValidation.isValid) {
      throw new Error(nameValidation.error);
    }

    // 3. Validate description (optional but if provided, must be valid)
    if (productData.description) {
      const descValidation = validateProductDescription(productData.description);
      if (!descValidation.isValid) {
        throw new Error(descValidation.error);
      }
    }

    // 4. Validate prices
    if (productData.originalPrice != null) {
      const priceValidation = validatePrice(productData.originalPrice);
      if (!priceValidation.isValid) {
        throw new Error(`Original price: ${priceValidation.error}`);
      }
    }

    if (productData.discountPrice != null) {
      const priceValidation = validatePrice(productData.discountPrice);
      if (!priceValidation.isValid) {
        throw new Error(`Discount price: ${priceValidation.error}`);
      }
    }

    if (productData.price != null) {
      const priceValidation = validatePrice(productData.price);
      if (!priceValidation.isValid) {
        throw new Error(`Price: ${priceValidation.error}`);
      }
    }

    // 5. Validate stock (if provided)
    if (productData.stock != null) {
      const stockValidation = validateStock(productData.stock);
      if (!stockValidation.isValid) {
        throw new Error(stockValidation.error);
      }
    }

    // 6. Validate tags (if provided)
    if (productData.tags) {
      const tagsValidation = validateTags(productData.tags);
      if (!tagsValidation.isValid) {
        throw new Error(tagsValidation.error);
      }
    }

    // 7. Validate image count
    const images = productData.images || [];
    if (images.length > VALIDATION_LIMITS.PRODUCT.MAX_IMAGES) {
      throw new Error(`Maximum ${VALIDATION_LIMITS.PRODUCT.MAX_IMAGES} images allowed per product`);
    }

    // 8. Check total product count limit
    const allProducts = await getProducts(0, false);
    if (allProducts.length >= VALIDATION_LIMITS.MAX_TOTAL_PRODUCTS) {
      throw new Error(`Maximum product limit reached (${VALIDATION_LIMITS.MAX_TOTAL_PRODUCTS} products). Please contact administrator.`);
    }

    // 9. Rate limiting check
    const rateLimitCheck = checkProductCreationRateLimit();
    if (!rateLimitCheck.allowed) {
      throw new Error(rateLimitCheck.message);
    }

    // 10. Generate or validate product ID/slug
    let productId = productData.id;
    let productSlug = productData.slug;

    if (!productId) {
      // Generate ID from category and name
      const category = productData.category || 'general';
      const nameWords = productData.name.toLowerCase().split(' ').slice(0, 2);
      const prefix = category.substring(0, 3).toUpperCase();
      const suffix = nameWords.map(word => word.substring(0, 2)).join('').toUpperCase();
      productId = `${prefix}-${suffix}`;

      let counter = 1;
      let finalId = productId;
      while (await productExists(finalId)) {
        finalId = `${productId}-${counter.toString().padStart(3, '0')}`;
        counter++;
      }
      productId = finalId;
    }

    // Generate slug if not provided
    if (!productSlug) {
      productSlug = generateProductSlug(productData.name);
      // Ensure slug is unique by appending productId if needed
      if (await productExists(productSlug)) {
        productSlug = `${productSlug}-${productId}`;
      }
    } else {
      // Validate provided slug
      const slugValidation = validateProductSlug(productSlug);
      if (!slugValidation.isValid) {
        throw new Error(`Invalid slug: ${slugValidation.error}`);
      }
      productSlug = slugValidation.value;
    }

    // Import helper to clean product data (remove base64)
    const { prepareProductDataForFirestore } = await import('../utils/firestoreImageHelpers');

    // Clean product data - removes base64, keeps only Storage URLs
    const cleanProductData = prepareProductDataForFirestore({
      ...productData,
      id: productId,
      slug: productSlug,
      name: nameValidation.value, // Use validated/trimmed name
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    // Compress product data if it exceeds 1MB (after cleaning base64)
    const compressedProductData = compressProductData(cleanProductData);

    const productRef = db.collection(PRODUCTS_COLLECTION).doc(productId);

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Add product request timeout')), 15000);
    });

    const setPromise = productRef.set(compressedProductData);

    await Promise.race([setPromise, timeoutPromise]);
    return productId;
  } catch (error) {
    console.error("Error adding product:", error);

    if (error.message.includes('timeout')) {
      throw new Error("Request timed out. Please check your internet connection and try again.");
    } else if (error.message.includes('permission')) {
      throw new Error("Permission denied. Please make sure you're logged in as an admin.");
    } else if (error.message.includes('network')) {
      throw new Error("Network error. Please check your connection and try again.");
    } else {
      throw new Error(`Failed to add product: ${error.message}`);
    }
  }
};

export const addProductWithId = async (productId, productData) => {
  try {
    // CRITICAL: Ensure Firebase is initialized before adding product
    const { ensureFirebaseInitialized, isFirebaseInitialized } = await import('./config');
    if (!isFirebaseInitialized() || !db) {
      console.log('⏳ Firebase not initialized, waiting...');
      await ensureFirebaseInitialized();
      if (!db) {
        throw new Error('Firebase database not initialized. Please refresh the page and try again.');
      }
    }

    // Import validation utilities
    const {
      VALIDATION_LIMITS,
      validateProductName,
      validateProductDescription,
      validatePrice,
      validateStock,
      validateTags,
      validateProductSlug,
      generateProductSlug
    } = await import('../utils/validationConstants');

    const { checkProductCreationRateLimit } = await import('../utils/rateLimiter');

    if (!productId) {
      throw new Error("Product ID is required");
    }

    // Validate product ID format (basic check)
    if (productId.length < 3 || productId.length > 100) {
      throw new Error("Product ID must be between 3 and 100 characters");
    }

    // ===== COMPREHENSIVE VALIDATION (same as addProduct) =====

    // 1. Required fields validation
    if (!productData.name || !productData.name.trim()) {
      throw new Error("Product name is required");
    }

    if (!productData.category || !productData.category.trim()) {
      throw new Error("Product category is required");
    }

    // At least one price must be provided
    const hasOriginalPrice = productData.originalPrice != null && productData.originalPrice > 0;
    const hasDiscountPrice = productData.discountPrice != null && productData.discountPrice > 0;
    const hasPrice = productData.price != null && productData.price > 0;

    if (!hasOriginalPrice && !hasDiscountPrice && !hasPrice) {
      throw new Error("At least one price (originalPrice, discountPrice, or price) is required");
    }

    // 2. Validate product name
    const nameValidation = validateProductName(productData.name);
    if (!nameValidation.isValid) {
      throw new Error(nameValidation.error);
    }

    // 3. Validate description (optional but if provided, must be valid)
    if (productData.description) {
      const descValidation = validateProductDescription(productData.description);
      if (!descValidation.isValid) {
        throw new Error(descValidation.error);
      }
    }

    // 4. Validate prices
    if (productData.originalPrice != null) {
      const priceValidation = validatePrice(productData.originalPrice);
      if (!priceValidation.isValid) {
        throw new Error(`Original price: ${priceValidation.error}`);
      }
    }

    if (productData.discountPrice != null) {
      const priceValidation = validatePrice(productData.discountPrice);
      if (!priceValidation.isValid) {
        throw new Error(`Discount price: ${priceValidation.error}`);
      }
    }

    if (productData.price != null) {
      const priceValidation = validatePrice(productData.price);
      if (!priceValidation.isValid) {
        throw new Error(`Price: ${priceValidation.error}`);
      }
    }

    // 5. Validate stock (if provided)
    if (productData.stock != null) {
      const stockValidation = validateStock(productData.stock);
      if (!stockValidation.isValid) {
        throw new Error(stockValidation.error);
      }
    }

    // 6. Validate tags (if provided)
    if (productData.tags) {
      const tagsValidation = validateTags(productData.tags);
      if (!tagsValidation.isValid) {
        throw new Error(tagsValidation.error);
      }
    }

    // 7. Validate image count
    const images = productData.images || [];
    if (images.length > VALIDATION_LIMITS.PRODUCT.MAX_IMAGES) {
      throw new Error(`Maximum ${VALIDATION_LIMITS.PRODUCT.MAX_IMAGES} images allowed per product`);
    }

    // 8. Check total product count limit
    const allProducts = await getProducts(0, false);
    if (allProducts.length >= VALIDATION_LIMITS.MAX_TOTAL_PRODUCTS) {
      throw new Error(`Maximum product limit reached (${VALIDATION_LIMITS.MAX_TOTAL_PRODUCTS} products). Please contact administrator.`);
    }

    // 9. Rate limiting check
    const rateLimitCheck = checkProductCreationRateLimit();
    if (!rateLimitCheck.allowed) {
      throw new Error(rateLimitCheck.message);
    }

    // Check if product with this ID already exists
    if (await productExists(productId)) {
      throw new Error(`Product with ID "${productId}" already exists`);
    }

    // Generate slug if not provided
    let productSlug = productData.slug;
    if (!productSlug) {
      productSlug = generateProductSlug(productData.name);
      // Ensure slug is unique by appending productId if needed
      if (await productExists(productSlug)) {
        productSlug = `${productSlug}-${productId}`;
      }
    } else {
      // Validate provided slug
      const slugValidation = validateProductSlug(productSlug);
      if (!slugValidation.isValid) {
        throw new Error(`Invalid slug: ${slugValidation.error}`);
      }
      productSlug = slugValidation.value;
    }

    // Import helper to clean product data (remove base64)
    const { prepareProductDataForFirestore } = await import('../utils/firestoreImageHelpers');

    // CRITICAL: Log product data before cleaning to debug image issues
    console.log('🔍 Product data before cleaning (addProductWithId):', {
      hasImageUrl: !!productData.imageUrl,
      imageUrlType: productData.imageUrl ? (productData.imageUrl.startsWith('https://') ? 'Storage URL' : productData.imageUrl.startsWith('data:') ? 'Base64' : 'Other') : 'None',
      hasImages: !!productData.images,
      imagesCount: productData.images?.length || 0
    });

    // Clean product data - removes base64, keeps only Storage URLs
    const cleanProductData = prepareProductDataForFirestore({
      ...productData,
      id: productId,
      slug: productSlug,
      name: nameValidation.value, // Use validated/trimmed name
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    // CRITICAL: Verify images are preserved after cleaning
    console.log('🔍 Product data after cleaning (addProductWithId):', {
      hasImageUrl: !!cleanProductData.imageUrl,
      imageUrl: cleanProductData.imageUrl?.substring(0, 50) || 'None',
      hasImages: !!cleanProductData.images,
      imagesCount: cleanProductData.images?.length || 0
    });

    // If images were removed but productData had images, warn but continue
    // (This should not happen for new products with uploaded images)
    if (productData.images && productData.images.length > 0 && (!cleanProductData.images || cleanProductData.images.length === 0)) {
      console.warn('⚠️ WARNING: All images were removed during cleaning!');
      console.warn('This should not happen for new products with uploaded images.');
      // Ensure at least imageUrl is preserved if it's a Storage URL
      if (productData.imageUrl && productData.imageUrl.startsWith('https://')) {
        cleanProductData.imageUrl = productData.imageUrl;
      }
    }

    // Compress product data if it exceeds 1MB (after cleaning base64)
    const compressedProductData = compressProductData(cleanProductData);

    const productRef = db.collection(PRODUCTS_COLLECTION).doc(productId);

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Add product request timeout')), 15000);
    });

    console.log('💾 Attempting to save product to Firestore:', {
      productId,
      hasName: !!compressedProductData.name,
      name: compressedProductData.name,
      hasCategory: !!compressedProductData.category,
      category: compressedProductData.category,
      hasPrice: !!compressedProductData.price,
      price: compressedProductData.price,
      imageUrl: compressedProductData.imageUrl?.substring(0, 50) || 'None',
      imagesCount: compressedProductData.images?.length || 0,
      dataSize: `${(JSON.stringify(compressedProductData).length / 1024).toFixed(2)}KB`,
      hasDb: !!db,
      dbType: db ? typeof db : 'null'
    });

    // CRITICAL: Double-check db is available before saving
    if (!db) {
      throw new Error('Firebase database is not available. Please refresh the page and try again.');
    }

    // CRITICAL: Verify collection method exists
    if (typeof db.collection !== 'function') {
      throw new Error('Firestore collection method not available. Database may not be properly initialized.');
    }

    const setPromise = productRef.set(compressedProductData);

    try {
      await Promise.race([setPromise, timeoutPromise]);
      console.log('✅ Product data written to Firestore');
    } catch (raceError) {
      if (raceError.message.includes('timeout')) {
        throw new Error(`Product save timed out after 15 seconds. Please check your connection and try again.`);
      }
      throw raceError;
    }

    // Verify the product was saved
    console.log('🔍 Verifying product was saved...');
    const verifyRef = db.collection(PRODUCTS_COLLECTION).doc(productId);
    const verifySnap = await verifyRef.get();

    if (!verifySnap.exists) {
      console.error('❌ Product verification failed - document does not exist');
      throw new Error('Product was not saved to Firestore. Please try again.');
    }

    const verifiedData = verifySnap.data();
    console.log('✅ Product successfully saved and verified:', {
      productId,
      name: verifiedData.name,
      hasImageUrl: !!verifiedData.imageUrl,
      imagesCount: verifiedData.images?.length || 0
    });

    return productId;
  } catch (error) {
    console.error("❌ Error adding product with ID:", error);
    console.error("Error details:", {
      message: error.message,
      code: error.code,
      stack: error.stack,
      productId,
      dbInitialized: db ? true : false
    });

    if (error.message.includes('timeout')) {
      throw new Error("Request timed out. Please check your internet connection and try again.");
    } else if (error.message.includes('permission') || error.code === 'permission-denied') {
      throw new Error("Permission denied. Please make sure you're logged in as an admin and have write permissions.");
    } else if (error.message.includes('network') || error.code === 'unavailable') {
      throw new Error("Network error. Please check your connection and try again.");
    } else {
      throw new Error(`Failed to add product: ${error.message}`);
    }
  }
};

export const updateProduct = async (productId, productData, retryCount = 0) => {
  const maxRetries = 3;
  const baseTimeout = 15000;
  const retryDelay = 2000;

  try {
    if (!productId) {
      throw new Error("Product ID is required for update");
    }

    if (productData.price !== undefined && (typeof productData.price !== 'number' || productData.price <= 0)) {
      throw new Error("Price must be a positive number");
    }

    const productRef = db.collection(PRODUCTS_COLLECTION).doc(productId);

    const productSnap = await productRef.get();

    if (!productSnap.exists) {
      console.error(`Product with ID "${productId}" does not exist in Firebase`);
      throw new Error(`Product with ID "${productId}" not found. Please check the product ID or create a new product.`);
    }

    // Get existing product data to merge with updates
    const existingData = productSnap.data();
    const mergedData = { ...existingData, ...productData };

    // Import helper to clean product data (remove base64)
    const { prepareProductDataForFirestore } = await import('../utils/firestoreImageHelpers');

    // Clean merged data - removes base64, keeps only Storage URLs
    const cleanMergedData = prepareProductDataForFirestore({
      ...mergedData,
      updatedAt: new Date(),
    });

    // Compress product data if it exceeds 1MB (after cleaning base64)
    const compressedProductData = compressProductData(cleanMergedData);

    const timeout = baseTimeout + (retryCount * 5000);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out. Please check your internet connection and try again.')), timeout);
    });

    const updatePromise = productRef.update(compressedProductData);

    await Promise.race([updatePromise, timeoutPromise]);
  } catch (error) {
    console.error(`Error updating product (attempt ${retryCount + 1}):`, error);

    if (retryCount < maxRetries && (error.message.includes('timeout') || error.message.includes('network') || error.message.includes('unavailable'))) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return updateProduct(productId, productData, retryCount + 1);
    }

    if (error.message.includes('timeout')) {
      throw new Error(`Request timed out after ${maxRetries + 1} attempts. Please check your internet connection and try again.`);
    } else if (error.message.includes('permission')) {
      throw new Error("Permission denied. Please make sure you're logged in as an admin.");
    } else if (error.message.includes('not-found')) {
      throw new Error("Product not found. It may have been deleted.");
    } else if (error.message.includes('network') || error.message.includes('unavailable')) {
      throw new Error("Network error. Please check your connection and try again.");
    } else {
      throw new Error(`Failed to update product: ${error.message}`);
    }
  }
};

export const deleteProduct = async (productId) => {
  try {
    const productRef = db.collection(PRODUCTS_COLLECTION).doc(productId);
    await productRef.delete();
  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
};

// Orders operations
export const createOrder = async (orderData) => {
  try {
    if (!orderData.orderId) {
      throw new Error("Order ID is required");
    }
    if (!orderData.customerInfo || !orderData.customerInfo.email) {
      throw new Error("Customer email is required");
    }
    if (!orderData.paymentInfo || !orderData.paymentInfo.paymentId) {
      // Payment ID missing, using order ID as fallback
    }

    const ordersRef = db.collection(ORDERS_COLLECTION);
    const docRef = await ordersRef.add({
      ...orderData,
      status: "pending",
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { id: docRef.id };
  } catch (error) {
    console.error("Error creating order:", error);
    throw error;
  }
};

export const getUserOrders = async (userId) => {
  try {
    const ordersRef = db.collection(ORDERS_COLLECTION);

    let allOrders = [];
    let useFallback = false;

    try {
      const userIdQuery = ordersRef.where('userId', '==', userId);
      const userIdSnapshot = await userIdQuery.get();

      const customerEmailQuery = ordersRef.where('customerInfo.email', '==', userId);
      const customerEmailSnapshot = await customerEmailQuery.get();

      const customerEmailAltQuery = ordersRef.where('customerEmail', '==', userId);
      const customerEmailAltSnapshot = await customerEmailAltQuery.get();

      const loggedInUserEmailQuery = ordersRef.where('loggedInUserEmail', '==', userId);
      const loggedInUserEmailSnapshot = await loggedInUserEmailQuery.get();

      const allOrdersMap = new Map();

      const cleanOrderData = (doc) => {
        const data = doc.data();
        const { shiprocketStatus, shiprocketOrderId, shiprocketAWB, ...cleanData } = data;

        if (data.orderStatus && typeof data.orderStatus === 'object') {
          cleanData.orderStatus = data.orderStatus.status || data.orderStatus.name || 'pending';
        }

        return { id: doc.id, ...cleanData };
      };

      userIdSnapshot.docs.forEach(doc => {
        allOrdersMap.set(doc.id, cleanOrderData(doc));
      });

      customerEmailSnapshot.docs.forEach(doc => {
        allOrdersMap.set(doc.id, cleanOrderData(doc));
      });

      customerEmailAltSnapshot.docs.forEach(doc => {
        allOrdersMap.set(doc.id, cleanOrderData(doc));
      });

      loggedInUserEmailSnapshot.docs.forEach(doc => {
        allOrdersMap.set(doc.id, cleanOrderData(doc));
      });

      allOrders = Array.from(allOrdersMap.values());

    } catch (queryError) {
      useFallback = true;
    }

    if (useFallback || allOrders.length === 0) {
      try {
        const allOrdersSnapshot = await ordersRef.get();
        allOrders = allOrdersSnapshot.docs
          .map(doc => {
            const data = doc.data();
            const { shiprocketStatus, shiprocketOrderId, shiprocketAWB, ...cleanData } = data;

            if (data.orderStatus && typeof data.orderStatus === 'object') {
              cleanData.orderStatus = data.orderStatus.status || data.orderStatus.name || 'pending';
            }

            return { id: doc.id, ...cleanData };
          })
          .filter(order => {
            const matches =
              order.userId === userId ||
              order.customerEmail === userId ||
              order.customerInfo?.email === userId ||
              order.loggedInUserEmail === userId;

            if (matches) { }

            return matches;
          });
      } catch (fallbackError) {
        console.error('Fallback approach also failed:', fallbackError);
        throw fallbackError;
      }
    }

    const sortedOrders = allOrders.sort((a, b) => {
      const dateA = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt || a.timestamp);
      const dateB = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt || b.timestamp);
      return dateB - dateA;
    });
    return sortedOrders;
  } catch (error) {
    console.error("Error getting user orders:", error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const orderRef = db.collection(ORDERS_COLLECTION).doc(orderId);
    await orderRef.update({
      status,
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
};

export const getOrderByOrderId = async (orderId) => {
  try {
    const ordersRef = db.collection(ORDERS_COLLECTION);
    const snapshot = await ordersRef.where('orderId', '==', orderId).limit(1).get();

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting order by orderId:", error);
    throw error;
  }
};

// Contact messages
export const addContactMessage = async (messageData) => {
  try {
    const messagesRef = db.collection(CONTACT_MESSAGES_COLLECTION);
    const docRef = await messagesRef.add({
      ...messageData,
      read: false,
      createdAt: new Date(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error adding contact message:", error);
    throw error;
  }
};

// Newsletter Signups operations
export const addNewsletterSignup = async (signupData) => {
  try {
    if (!isFirebaseInitialized() || !db) {
      console.error("❌ Firebase not initialized");
      throw new Error("Firebase is not initialized");
    }

    // Validate required fields - email is required, whatsapp can be placeholder
    if (!signupData.email) {
      console.error("❌ Missing required field: email");
      throw new Error("Email is required");
    }

    // Use placeholder if whatsapp is missing
    const whatsappValue = signupData.whatsapp || '0000000000';

    const signupPayload = {
      email: signupData.email.toLowerCase().trim(),
      whatsapp: whatsappValue.trim(),
      countryCode: signupData.countryCode || "+91",
      fullPhone: signupData.fullPhone || `${signupData.countryCode || "+91"}${whatsappValue.trim()}`,
      name: signupData.name || "",
      userId: signupData.userId || null,
      source: signupData.source || "popup", // 'popup', 'signup', 'account', 'login', 'google-login'
      subscribed: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const signupsRef = db.collection(NEWSLETTER_SIGNUPS_COLLECTION);
    const docRef = await signupsRef.add(signupPayload);

    return docRef.id;
  } catch (error) {
    console.error("❌ Error adding newsletter signup:", error);
    console.error("❌ Error details:", {
      code: error.code,
      message: error.message,
      stack: error.stack
    });

    // Provide more helpful error messages
    if (error.code === 'permission-denied') {
      throw new Error("Permission denied. Please check Firestore security rules.");
    } else if (error.code === 'unavailable') {
      throw new Error("Firestore is unavailable. Please check your internet connection.");
    } else {
      throw error;
    }
  }
};

export const isUserSubscribed = async (email) => {
  try {
    if (!isFirebaseInitialized() || !db || !email) {
      return false;
    }

    const emailLower = email.toLowerCase().trim();

    const signupsRef = db.collection(NEWSLETTER_SIGNUPS_COLLECTION);
    const query = signupsRef
      .where("email", "==", emailLower)
      .where("subscribed", "==", true)
      .limit(1);

    const snapshot = await query.get();
    return !snapshot.empty;
  } catch (error) {
    console.error("❌ Error checking subscription:", error);
    console.error("❌ Subscription check error details:", {
      code: error.code,
      message: error.message
    });
    return false;
  }
};

export const getUserNewsletterStatus = async (userId) => {
  try {
    if (!isFirebaseInitialized() || !db || !userId) {
      return null;
    }

    const signupsRef = db.collection(NEWSLETTER_SIGNUPS_COLLECTION);
    const query = signupsRef
      .where("userId", "==", userId)
      .where("subscribed", "==", true)
      .limit(1);
    const snapshot = await query.get();

    if (!snapshot.empty) {
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting user newsletter status:", error);
    return null;
  }
};

// Founder Videos operations
export const getFounderVideos = async () => {
  try {
    // Check if Firebase is initialized
    if (!isFirebaseInitialized() || !db) {
      return [];
    }

    const videosRef = db.collection(FOUNDER_VIDEOS_COLLECTION);
    const q = videosRef.orderBy("order", "asc");
    const snapshot = await q.get();
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error getting founder videos:", error);
    // Return empty array instead of throwing to prevent app crashes
    return [];
  }
};

// Subscribe to founder videos - Real-time listener
export const subscribeToFounderVideos = (callback) => {
  // Wait for Firebase initialization
  let unsubscribe = null;
  let isSubscribed = false;

  const setupSubscription = async () => {
    try {
      const { db: firestoreDb } = await ensureFirebaseInitialized();
      if (!firestoreDb) {
        console.warn('⚠️ Firestore database not initialized');
        callback([]);
        return;
      }

      if (isSubscribed) return; // Already subscribed

      const videosRef = firestoreDb.collection(FOUNDER_VIDEOS_COLLECTION);
      const query = videosRef.orderBy("order", "asc");

      unsubscribe = query.onSnapshot(
        (snapshot) => {
          const videos = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }));
          callback(videos);
        },
        (error) => {
          console.error("Error in founder videos subscription:", error);
          callback([]);
        }
      );

      isSubscribed = true;
    } catch (error) {
      console.error("Error setting up founder videos subscription:", error);
      callback([]);
    }
  };

  // Start subscription setup
  setupSubscription();

  // Return unsubscribe function
  return () => {
    if (unsubscribe) {
      unsubscribe();
      isSubscribed = false;
    }
  };
};

export const addFounderVideo = async (videoData) => {
  try {
    if (!videoData.title) {
      throw new Error("Missing required field: title is required");
    }

    if (videoData.type === 'instagram' || videoData.type === 'youtube' || videoData.type === 'clean-video') {
      if (!videoData.instagramUrl) {
        const platform = videoData.type === 'instagram' ? 'Instagram' :
          videoData.type === 'youtube' ? 'YouTube' : 'Video';
        throw new Error(`Missing required field: ${platform} URL is required for ${platform} videos`);
      }
    } else {
      if (!videoData.src) {
        throw new Error("Missing required field: video source is required for video files");
      }
    }

    if (videoData.type !== 'instagram' && videoData.type !== 'youtube' && videoData.type !== 'clean-video') {
      const estimatedSize = JSON.stringify(videoData).length;
      if (estimatedSize > 1000000) {
        throw new Error("Video data is too large. Please compress your video file to under 1MB.");
      }
    }

    const videosRef = db.collection(FOUNDER_VIDEOS_COLLECTION);
    const docRef = await videosRef.add({
      ...videoData,
      order: videoData.order || 0,
      isActive: videoData.isActive !== undefined ? videoData.isActive : true,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error adding founder video:", error);
    throw error;
  }
};

export const updateFounderVideo = async (videoId, videoData) => {
  try {
    if (!videoData.title) {
      throw new Error("Missing required field: title is required");
    }

    if (videoData.type === 'instagram' || videoData.type === 'youtube' || videoData.type === 'clean-video') {
      if (!videoData.instagramUrl) {
        const platform = videoData.type === 'instagram' ? 'Instagram' :
          videoData.type === 'youtube' ? 'YouTube' : 'Video';
        throw new Error(`Missing required field: ${platform} URL is required for ${platform} videos`);
      }
    } else {
      if (!videoData.src) {
        throw new Error("Missing required field: video source is required for video files");
      }
    }

    if (videoData.type !== 'instagram' && videoData.type !== 'youtube' && videoData.type !== 'clean-video') {
      const estimatedSize = JSON.stringify(videoData).length;
      if (estimatedSize > 1000000) {
        throw new Error("Video data is too large. Please compress your video file to under 1MB.");
      }
    }

    const videoRef = db.collection(FOUNDER_VIDEOS_COLLECTION).doc(videoId);
    await videoRef.update({
      ...videoData,
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error updating founder video:", error);
    throw error;
  }
};

export const deleteFounderVideo = async (videoId) => {
  try {
    const videoRef = db.collection(FOUNDER_VIDEOS_COLLECTION).doc(videoId);
    await videoRef.delete();
  } catch (error) {
    console.error("Error deleting founder video:", error);
    throw error;
  }
};

// User operations
export const createUserProfile = async (userData) => {
  try {
    const userRef = db.collection('users').doc(userData.uid);

    await userRef.set({
      ...userData,
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date(),
      status: 'active'
    });
    return userData.uid;
  } catch (error) {
    console.error("Error creating user profile:", error);
    throw error;
  }
};

export const updateUserProfile = async (userId, userData) => {
  try {
    const userRef = db.collection('users').doc(userId);
    await userRef.update({
      ...userData,
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export const getUserProfile = async (userId) => {
  try {
    const userRef = db.collection('users').doc(userId);
    const userSnap = await userRef.get();

    if (userSnap.exists) {
      return { id: userSnap.id, ...userSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting user profile:", error);
    throw error;
  }
};

// Recent searches operations
export const saveUserRecentSearches = async (userId, searches) => {
  try {
    const userRef = db.collection('users').doc(userId);
    await userRef.update({
      recentSearches: searches,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error("Error saving recent searches:", error);
    throw error;
  }
};

export const getUserRecentSearches = async (userId) => {
  try {
    const userRef = db.collection('users').doc(userId);
    const userSnap = await userRef.get();
    return userSnap.exists ? (userSnap.data().recentSearches || []) : [];
  } catch (error) {
    console.error("Error getting recent searches:", error);
    return [];
  }
};

export const getAllUsers = async () => {
  try {
    const usersRef = db.collection('users');
    const q = usersRef.orderBy('createdAt', 'desc');
    const snapshot = await q.get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error getting all users:", error);
    throw error;
  }
};

// Cart operations
export const saveUserCart = async (userId, cartItems) => {
  // Rate limit removed - Firestore handles its own rate limiting
  // Cart operations are user-specific and shouldn't be blocked

  try {
    // Minimize cart items to reduce document size
    const minimizedCartItems = cartItems.map(item => {
      // If item is already minimized (doesn't have large fields), return as is
      if (!item.images && !item.base64Data && !item.colorVariants) {
        return item;
      }
      // Otherwise minimize it
      return minimizeProductForCart(item);
    });

    // Remove any undefined values before saving (Firestore doesn't allow undefined)
    const cleanedCartItems = removeUndefinedValues(minimizedCartItems);

    const cartRef = db.collection(CARTS_COLLECTION).doc(userId);

    // Use default source - faster (cache first), then server
    // Only check existence if needed, otherwise use set with merge
    const cartSnap = await cartRef.get();

    if (cartSnap.exists) {
      await cartRef.update({
        items: cleanedCartItems,
        updatedAt: new Date(),
      });
    } else {
      await cartRef.set({
        userId,
        items: cleanedCartItems,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
  } catch (error) {
    console.error("Error saving cart:", error);

    // Handle document size error - minimize and retry
    if (error.message && error.message.includes('exceeds the maximum allowed size')) {
      console.warn('⚠️ Cart document too large, attempting to minimize and retry...');
      try {
        // Force minimize all items
        const minimizedCartItems = cartItems.map(item => minimizeProductForCart(item));
        // Remove any undefined values before saving
        const cleanedCartItems = removeUndefinedValues(minimizedCartItems);
        const cartRef = db.collection(CARTS_COLLECTION).doc(userId);
        const cartSnap = await cartRef.get();

        if (cartSnap.exists) {
          await cartRef.update({
            items: cleanedCartItems,
            updatedAt: new Date(),
          });
        } else {
          await cartRef.set({
            userId,
            items: cleanedCartItems,
            createdAt: new Date(),
            updatedAt: new Date(),
          });
        }
        return; // Success after minimization
      } catch (retryError) {
        console.error("Error retrying with minimized cart:", retryError);
        throw retryError;
      }
    }

    // Handle resource-exhausted error - don't queue more writes
    if (error.code === 'resource-exhausted') {
      console.warn('⚠️ Firestore write queue exhausted. Skipping save to prevent further queuing.');
      return; // Don't retry or queue more writes
    }

    // If offline error, use set with merge as fallback (works offline, queues write)
    if (error.code === 'unavailable' ||
      error.message.includes('offline') ||
      error.message.includes('Failed to get document because the client is offline')) {
      try {
        const cartRef = db.collection(CARTS_COLLECTION).doc(userId);
        // Minimize before saving offline
        const minimizedCartItems = cartItems.map(item => {
          if (!item.images && !item.base64Data && !item.colorVariants) {
            return item;
          }
          return minimizeProductForCart(item);
        });
        // Remove any undefined values before saving
        const cleanedCartItems = removeUndefinedValues(minimizedCartItems);
        // Use set with merge - this works offline and queues the write
        await cartRef.set({
          userId,
          items: cleanedCartItems,
          updatedAt: new Date(),
        }, { merge: true });
        return; // Success - write is queued
      } catch (fallbackError) {
        console.error("Error in cart fallback save:", fallbackError);
      }
    }

    if (error.code === 'permission-denied') {
      return;
    }

    throw error;
  }
};

/**
 * Safe helper to get user cart with offline/cache fallback
 * Never throws - always returns a safe default
 */
export const getUserCart = async (userId) => {
  if (!db || !userId) {
    return [];
  }

  const cartRef = db.collection(CARTS_COLLECTION).doc(userId);

  try {
    // Try to get from server (or cache if offline)
    const cartSnap = await cartRef.get();

    if (cartSnap.exists) {
      return cartSnap.data().items || [];
    }
    return [];
  } catch (error) {
    // Check if it's an offline/unavailable error
    const isOfflineError = error.code === 'unavailable' ||
      error.message?.includes('offline') ||
      error.message?.includes('client is offline') ||
      error.message?.includes('Failed to get document because the client is offline');

    if (isOfflineError) {
      // Try to get from cache as fallback
      try {
        const cachedSnap = await cartRef.get({ source: 'cache' });
        if (cachedSnap.exists) {
          console.log('✅ Loaded cart from cache (offline mode)');
          return cachedSnap.data().items || [];
        }
      } catch (cacheError) {
        // Cache read failed - that's okay, we'll return empty array
        console.warn('⚠️ Could not load cart from cache:', cacheError.message);
      }

      // Return safe default - empty cart
      console.warn('⚠️ Cart temporarily unavailable (offline). Returning empty cart.');
      return [];
    }

    // For permission-denied, silently return empty (expected for guests)
    if (error.code === 'permission-denied') {
      return [];
    }
    // For other errors, log but still return safe default
    console.warn("Error getting user cart:", error.code || error.message);
    return [];
  }
};

// Wishlist operations
export const saveUserWishlist = async (userId, wishlistItems) => {
  // Rate limit removed - Firestore handles its own rate limiting
  // Wishlist operations are user-specific and shouldn't be blocked

  try {
    const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);

    // Use default source - faster (cache first), then server
    // Only check existence if needed, otherwise use set with merge
    const wishlistSnap = await wishlistRef.get();

    // Remove any undefined values from wishlist items before saving
    const cleanedWishlistItems = removeUndefinedValues(wishlistItems);

    // Prepare wishlist document
    const wishlistData = wishlistSnap.exists ? {
      items: cleanedWishlistItems,
      updatedAt: new Date(),
    } : {
      userId,
      items: cleanedWishlistItems,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Compress wishlist document if it exceeds 1MB
    const compressedWishlistData = compressWishlistDocument(wishlistData);

    if (wishlistSnap.exists) {
      await wishlistRef.update(compressedWishlistData);
    } else {
      await wishlistRef.set(compressedWishlistData);
    }
  } catch (error) {
    console.error("Error saving wishlist:", error);

    // Handle resource-exhausted error - don't queue more writes
    if (error.code === 'resource-exhausted') {
      console.warn('⚠️ Firestore write queue exhausted. Skipping save to prevent further queuing.');
      return; // Don't retry or queue more writes
    }

    // If offline error, use set with merge as fallback (works offline, queues write)
    if (error.code === 'unavailable' ||
      error.message.includes('offline') ||
      error.message.includes('Failed to get document because the client is offline')) {
      try {
        const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);
        // Use set with merge - this works offline and queues the write
        // Also compress before saving
        const wishlistData = {
          userId,
          items: removeUndefinedValues(wishlistItems),
          updatedAt: new Date(),
        };
        const compressedWishlistData = compressWishlistDocument(wishlistData);
        await wishlistRef.set(compressedWishlistData, { merge: true });
        return; // Success - write is queued
      } catch (fallbackError) {
        console.error("Error in wishlist fallback save:", fallbackError);
      }
    }

    if (error.code === 'permission-denied') {
      return;
    }

    throw error;
  }
};

/**
 * Safe helper to get user wishlist with offline/cache fallback
 * Never throws - always returns a safe default
 */
export const getUserWishlist = async (userId) => {
  if (!db || !userId) {
    return [];
  }

  const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);

  try {
    // Try to get from server (or cache if offline)
    const wishlistSnap = await wishlistRef.get();

    if (wishlistSnap.exists) {
      return wishlistSnap.data().items || [];
    }
    return [];
  } catch (error) {
    // Check if it's an offline/unavailable error
    const isOfflineError = error.code === 'unavailable' ||
      error.message?.includes('offline') ||
      error.message?.includes('client is offline') ||
      error.message?.includes('Failed to get document because the client is offline');

    if (isOfflineError) {
      // Try to get from cache as fallback
      try {
        const cachedSnap = await wishlistRef.get({ source: 'cache' });
        if (cachedSnap.exists) {
          console.log('✅ Loaded wishlist from cache (offline mode)');
          return cachedSnap.data().items || [];
        }
      } catch (cacheError) {
        // Cache read failed - that's okay, we'll return empty array
        console.warn('⚠️ Could not load wishlist from cache:', cacheError.message);
      }

      // Return safe default - empty wishlist
      console.warn('⚠️ Wishlist temporarily unavailable (offline). Returning empty wishlist.');
      return [];
    }

    // For permission-denied, silently return empty (expected for guests)
    if (error.code === 'permission-denied') {
      return [];
    }
    // For other errors, log but still return safe default
    console.warn("Error getting user wishlist:", error.code || error.message);
    return [];
  }
};

// Helper function to remove undefined values from an object (Firestore doesn't allow undefined)
const removeUndefinedValues = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item));
  }

  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = typeof value === 'object' ? removeUndefinedValues(value) : value;
    }
  }
  return cleaned;
};

// Helper function to minimize product data for cart storage
// This reduces the document size by storing only essential fields
const minimizeProductForCart = (product) => {
  // Get a single thumbnail image - preserve both base64Data and imageUrl for compatibility
  // CRITICAL: Always prioritize base64Data from main product (can be stored in Firestore)
  // Skip blob URLs (temporary browser URLs that can't be stored)
  let thumbnailImageUrl = null;
  let thumbnailBase64 = null;

  // Helper to check if a string is a blob URL (temporary, can't be stored)
  const isBlobUrl = (url) => url && typeof url === 'string' && url.startsWith('blob:');

  // Helper to check if a string is base64 data
  const isBase64Data = (str) => {
    if (!str || typeof str !== 'string') return false;
    return str.startsWith('data:image') ||
      (str.length > 1000 && /^[A-Za-z0-9+/=]+$/.test(str.replace(/^data:image\/[^;]+;base64,/, '')));
  };

  // PRIORITY 1: Check selectedVariant.base64Data first (user's selected variant)
  if (product.selectedVariant?.base64Data && typeof product.selectedVariant.base64Data === 'string' && product.selectedVariant.base64Data.length > 0) {
    thumbnailBase64 = product.selectedVariant.base64Data;
  }

  // PRIORITY 2: Check main product's base64Data
  if (!thumbnailBase64 && product.base64Data && typeof product.base64Data === 'string' && product.base64Data.length > 0) {
    thumbnailBase64 = product.base64Data;
  }

  // PRIORITY 3: Try to get image from images array (carousel images) - check base64Data first
  if (!thumbnailBase64 && product.images && Array.isArray(product.images) && product.images.length > 0) {
    const firstImage = product.images[0];
    if (firstImage && typeof firstImage === 'object' && firstImage.base64Data) {
      thumbnailBase64 = firstImage.base64Data;
    } else if (typeof firstImage === 'string' && !isBlobUrl(firstImage) && isBase64Data(firstImage)) {
      thumbnailBase64 = firstImage;
    }
  }

  // PRIORITY 4: Try to get image from selectedVariant.image (but skip blob URLs)
  if (!thumbnailBase64 && product.selectedVariant?.image) {
    const variantImage = product.selectedVariant.image;
    if (!isBlobUrl(variantImage)) {
      if (isBase64Data(variantImage)) {
        thumbnailBase64 = variantImage;
      } else {
        thumbnailImageUrl = variantImage;
      }
    }
  }

  // PRIORITY 5: Try to get image from colorVariants (but skip blob URLs)
  if (!thumbnailBase64 && !thumbnailImageUrl && product.colorVariants && product.colorVariants.length > 0) {
    const firstVariant = product.colorVariants[0];
    if (firstVariant.base64Data) {
      thumbnailBase64 = firstVariant.base64Data;
    } else if (firstVariant.image && !isBlobUrl(firstVariant.image)) {
      if (isBase64Data(firstVariant.image)) {
        thumbnailBase64 = firstVariant.image;
      } else {
        thumbnailImageUrl = firstVariant.image;
      }
    }
  }

  // PRIORITY 6: Fallback to main product's imageUrl (but skip blob URLs)
  if (!thumbnailBase64 && !thumbnailImageUrl && product.imageUrl && !isBlobUrl(product.imageUrl)) {
    if (isBase64Data(product.imageUrl)) {
      thumbnailBase64 = product.imageUrl;
    } else {
      thumbnailImageUrl = product.imageUrl;
    }
  }

  // PRIORITY 7: Check images array for imageUrl (but skip blob URLs)
  if (!thumbnailBase64 && !thumbnailImageUrl && product.images && product.images.length > 0) {
    const firstImage = product.images[0];
    if (typeof firstImage === 'string' && !isBlobUrl(firstImage)) {
      thumbnailImageUrl = firstImage;
    } else if (typeof firstImage === 'object' && firstImage.imageUrl && !isBlobUrl(firstImage.imageUrl)) {
      thumbnailImageUrl = firstImage.imageUrl;
    }
  }

  // PRIORITY 8: Check legacy img field (but skip blob URLs)
  if (!thumbnailBase64 && !thumbnailImageUrl && product.img && !isBlobUrl(product.img)) {
    if (isBase64Data(product.img)) {
      thumbnailBase64 = product.img;
    } else {
      thumbnailImageUrl = product.img;
    }
  }

  // Return only essential fields to minimize document size
  // Filter out undefined values (Firestore doesn't allow undefined)
  const minimized = {
    id: product.id,
    name: product.name,
    price: product.price,
    quantity: product.quantity || 1,
  };

  // Only add fields that are defined (not undefined)
  if (product.originalPrice !== undefined) {
    minimized.originalPrice = product.originalPrice;
  }
  if (product.discountPrice !== undefined) {
    minimized.discountPrice = product.discountPrice;
  }
  if (product.color !== undefined) {
    minimized.color = product.color;
  }
  if (product.zari !== undefined) {
    minimized.zari = product.zari;
  }
  if (product.occasion !== undefined) {
    minimized.occasion = product.occasion;
  }
  if (product.category !== undefined) {
    minimized.category = product.category;
  }
  if (product.categoryId !== undefined) {
    minimized.categoryId = product.categoryId;
  }
  if (product.stock !== undefined && product.stock !== null) {
    minimized.stock = product.stock;
  }

  // CRITICAL: Always preserve base64Data if available (getProductImage checks this first)
  // This ensures images are visible on checkout immediately after add-to-cart
  if (thumbnailBase64) {
    minimized.base64Data = thumbnailBase64;
  }
  // Also preserve imageUrl for compatibility (persistent URLs, not blob URLs)
  // This provides a fallback if base64Data is missing
  if (thumbnailImageUrl) {
    minimized.imageUrl = thumbnailImageUrl;
  }
  // If we have base64Data but no imageUrl, we still need imageUrl for getProductImage fallback
  // However, getProductImage will use base64Data first, so this is optional
  // But we ensure at least one image field is always present
  if (!minimized.base64Data && !minimized.imageUrl) {
    // Try to get imageUrl from normalized product (might have been set by normalizeProductImages)
    if (product.imageUrl && !product.imageUrl.startsWith('blob:')) {
      minimized.imageUrl = product.imageUrl;
    } else if (product.img && !product.img.startsWith('blob:')) {
      minimized.imageUrl = product.img;
    }
  }

  // Store selected variant info if available (minimal)
  if (product.selectedVariant) {
    minimized.selectedVariant = {};
    if (product.selectedVariant.color !== undefined) {
      minimized.selectedVariant.color = product.selectedVariant.color;
    }
    if (product.selectedVariant.id !== undefined) {
      minimized.selectedVariant.id = product.selectedVariant.id;
    }
    // Only include selectedVariant if it has at least one property
    if (Object.keys(minimized.selectedVariant).length === 0) {
      delete minimized.selectedVariant;
    }
  }

  return minimized;
};

// Helper function to minimize product data for wishlist storage
// Similar to minimizeProductForCart but without quantity field
const minimizeProductForWishlist = (product) => {
  // Reuse the same logic as cart minimization
  const minimized = minimizeProductForCart(product);
  // Remove quantity field for wishlist (not needed)
  delete minimized.quantity;
  return minimized;
};

// Helper function to compress wishlist document if it exceeds 1MB
const compressWishlistDocument = (wishlistData) => {
  const MAX_SIZE = 1024 * 1024; // 1MB in bytes
  const dataSize = calculateDataSize(wishlistData);

  if (dataSize <= MAX_SIZE) {
    return wishlistData; // No compression needed
  }

  // Create a copy to modify
  const compressed = { ...wishlistData };
  let currentSize = dataSize;

  // If items array exists and document is too large, remove base64Data from items
  // Start from the beginning (oldest items) to preserve recently added items
  if (compressed.items && Array.isArray(compressed.items) && compressed.items.length > 0) {
    const items = [...compressed.items]; // Create a copy to modify

    // Sort items by a timestamp if available, or process from start
    // Remove base64Data from items until document is under 1MB
    // Keep imageUrl as fallback
    for (let i = 0; i < items.length && currentSize > MAX_SIZE; i++) {
      const item = items[i];
      if (item && item.base64Data && typeof item.base64Data === 'string') {
        // Only remove base64Data if we have imageUrl as fallback
        if (item.imageUrl && !item.imageUrl.startsWith('blob:')) {
          const { base64Data, ...itemWithoutBase64 } = item;
          items[i] = itemWithoutBase64;
          // Recalculate size
          compressed.items = items;
          currentSize = calculateDataSize(compressed);
        }
      }
    }

    compressed.items = items;
  }

  // If still too large after removing base64Data, throw error
  if (currentSize > MAX_SIZE) {
    const sizeMB = (currentSize / (1024 * 1024)).toFixed(2);
    throw new Error(
      `Wishlist document is too large (${sizeMB}MB). Maximum size is 1MB. ` +
      `Please remove some items from your wishlist or contact support.`
    );
  }

  return compressed;
};

// Cart item operations
export const addItemToCart = async (userId, product, quantity = 1) => {
  try {
    // Fetch latest product data to get current stock and ensure we have image data
    let productStock = product.stock;
    let productWithImage = { ...product };

    try {
      const latestProduct = await getProductById(product.id);
      if (latestProduct) {
        // Update stock if available
        if (latestProduct.stock !== undefined && latestProduct.stock !== null) {
          productStock = Number(latestProduct.stock);
        }

        // Ensure we have image data - extract from multiple sources if needed
        // This ensures images are always available even if the passed product was normalized with blob URLs
        if (!productWithImage.base64Data) {
          // Priority 1: Check selectedVariant.base64Data (user's selected variant)
          if (productWithImage.selectedVariant?.base64Data) {
            productWithImage.base64Data = productWithImage.selectedVariant.base64Data;
          }
          // Priority 2: Check latestProduct.base64Data from Firestore
          else if (latestProduct.base64Data) {
            productWithImage.base64Data = latestProduct.base64Data;
          }
          // Priority 3: Extract from latestProduct.images array
          else if (latestProduct.images && Array.isArray(latestProduct.images) && latestProduct.images.length > 0) {
            const firstImage = latestProduct.images[0];
            if (firstImage && typeof firstImage === 'object' && firstImage.base64Data) {
              productWithImage.base64Data = firstImage.base64Data;
            } else if (typeof firstImage === 'string' &&
              !firstImage.startsWith('blob:') &&
              firstImage.length > 1000 &&
              /^data:image/.test(firstImage)) {
              productWithImage.base64Data = firstImage;
            }
          }
        }

        // Also preserve imageUrl if it's not a blob URL (fallback for getProductImage)
        if (!productWithImage.imageUrl || productWithImage.imageUrl.startsWith('blob:')) {
          // Check selectedVariant first
          if (productWithImage.selectedVariant?.image && !productWithImage.selectedVariant.image.startsWith('blob:')) {
            productWithImage.imageUrl = productWithImage.selectedVariant.image;
          }
          // Then check latestProduct.imageUrl
          else if (latestProduct.imageUrl && !latestProduct.imageUrl.startsWith('blob:')) {
            productWithImage.imageUrl = latestProduct.imageUrl;
          }
          // Then check images array
          else if (latestProduct.images && Array.isArray(latestProduct.images) && latestProduct.images.length > 0) {
            const firstImage = latestProduct.images[0];
            if (typeof firstImage === 'object' && firstImage.imageUrl && !firstImage.imageUrl.startsWith('blob:')) {
              productWithImage.imageUrl = firstImage.imageUrl;
            } else if (typeof firstImage === 'string' && !firstImage.startsWith('blob:')) {
              productWithImage.imageUrl = firstImage;
            }
          }
        }

        // Preserve images array if available and not blob URLs (for future use)
        if (latestProduct.images && Array.isArray(latestProduct.images) && latestProduct.images.length > 0) {
          const validImages = latestProduct.images.filter(img => {
            if (typeof img === 'string') {
              return !img.startsWith('blob:');
            }
            if (typeof img === 'object' && img.imageUrl) {
              return !img.imageUrl.startsWith('blob:');
            }
            return true;
          });
          if (validImages.length > 0 && (!productWithImage.images || productWithImage.images.length === 0)) {
            productWithImage.images = validImages;
          }
        }
      }
    } catch (err) {
      // If we can't fetch product, use stock from product object passed in
      console.warn('Could not fetch latest product data, using provided product data');
    }

    const cartRef = db.collection(CARTS_COLLECTION).doc(userId);
    const cartSnap = await cartRef.get();

    let cartItems = [];
    if (cartSnap.exists) {
      cartItems = cartSnap.data().items || [];
    }

    const existingItemIndex = cartItems.findIndex(item => item.id === product.id);

    // Validate stock before adding
    if (productStock !== undefined && productStock !== null) {
      if (productStock <= 0) {
        throw new Error(`${product.name || 'Product'} is out of stock!`);
      }

      const currentCartQuantity = existingItemIndex >= 0 ? cartItems[existingItemIndex].quantity : 0;
      const newTotalQuantity = currentCartQuantity + quantity;

      if (newTotalQuantity > productStock) {
        const availableToAdd = productStock - currentCartQuantity;
        if (availableToAdd <= 0) {
          throw new Error(`Only ${productStock} ${productStock === 1 ? 'item' : 'items'} available in stock. Already in cart!`);
        } else {
          // Adjust quantity to available stock
          quantity = availableToAdd;
        }
      }
    }

    // Minimize product data before storing (now with ensured image data)
    const minimizedProduct = minimizeProductForCart({ ...productWithImage, quantity });

    // DEBUG: Log to verify base64Data is preserved
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 addItemToCart - minimizedProduct:', {
        id: minimizedProduct.id,
        name: minimizedProduct.name,
        hasBase64Data: !!minimizedProduct.base64Data,
        base64DataLength: minimizedProduct.base64Data?.length,
        hasImageUrl: !!minimizedProduct.imageUrl
      });
    }

    if (existingItemIndex >= 0) {
      cartItems[existingItemIndex].quantity += quantity;
      // Ensure quantity doesn't exceed stock (double check)
      if (productStock !== undefined && productStock !== null && cartItems[existingItemIndex].quantity > productStock) {
        cartItems[existingItemIndex].quantity = productStock;
      }
      // Update other fields in case they changed
      // CRITICAL: Preserve base64Data from minimizedProduct (it should have it from our fixes)
      cartItems[existingItemIndex] = {
        ...cartItems[existingItemIndex],
        ...minimizedProduct,
        quantity: cartItems[existingItemIndex].quantity,
        // Ensure base64Data is explicitly preserved (minimizedProduct should have it, but double-check)
        base64Data: minimizedProduct.base64Data || cartItems[existingItemIndex].base64Data
      };
    } else {
      cartItems.push(minimizedProduct);
    }

    // Remove any undefined values from cart items before saving (Firestore doesn't allow undefined)
    const cleanedCartItems = removeUndefinedValues(cartItems);

    if (cartSnap.exists) {
      await cartRef.update({
        items: cleanedCartItems,
        updatedAt: new Date(),
      });
    } else {
      await cartRef.set({
        userId,
        items: cleanedCartItems,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }

    return cartItems;
  } catch (error) {
    console.error("Error adding item to cart:", error);
    throw error;
  }
};

export const removeItemFromCart = async (userId, productId) => {
  try {
    const cartRef = db.collection(CARTS_COLLECTION).doc(userId);
    const cartSnap = await cartRef.get();

    if (!cartSnap.exists) {
      return [];
    }

    let cartItems = cartSnap.data().items || [];
    cartItems = cartItems.filter(item => item.id !== productId);

    // Remove any undefined values before saving (Firestore doesn't allow undefined)
    const cleanedCartItems = removeUndefinedValues(cartItems);

    await cartRef.update({
      items: cleanedCartItems,
      updatedAt: new Date(),
    });

    return cleanedCartItems;
  } catch (error) {
    console.error("Error removing item from cart:", error);
    throw error;
  }
};

export const updateCartItemQuantity = async (userId, productId, quantity) => {
  try {
    const cartRef = db.collection(CARTS_COLLECTION).doc(userId);
    const cartSnap = await cartRef.get();

    if (!cartSnap.exists) {
      return [];
    }

    let cartItems = cartSnap.data().items || [];
    const itemIndex = cartItems.findIndex(item => item.id === productId);

    if (itemIndex >= 0) {
      if (quantity <= 0) {
        cartItems.splice(itemIndex, 1);
      } else {
        const cartItem = cartItems[itemIndex];

        // Validate stock before updating quantity
        let productStock = cartItem.stock;
        try {
          const latestProduct = await getProductById(productId);
          if (latestProduct && latestProduct.stock !== undefined && latestProduct.stock !== null) {
            productStock = Number(latestProduct.stock);
          }
        } catch (err) {
          // If we can't fetch product, use stock from cart item
          console.warn('Could not fetch latest product stock, using cart item stock value');
        }

        if (productStock !== undefined && productStock !== null) {
          if (productStock <= 0) {
            throw new Error(`${cartItem.name || 'Product'} is out of stock!`);
          }

          if (quantity > productStock) {
            throw new Error(`Only ${productStock} ${productStock === 1 ? 'item' : 'items'} available in stock!`);
          }
        }

        cartItems[itemIndex].quantity = quantity;
      }
    }

    // Remove any undefined values before saving (Firestore doesn't allow undefined)
    const cleanedCartItems = removeUndefinedValues(cartItems);

    await cartRef.update({
      items: cleanedCartItems,
      updatedAt: new Date(),
    });

    return cleanedCartItems;
  } catch (error) {
    console.error("Error updating cart item quantity:", error);
    throw error;
  }
};

// Wishlist item operations
export const addItemToWishlist = async (userId, product) => {
  try {
    // Fetch latest product data to ensure we have image data
    let productWithImage = product;
    try {
      const latestProduct = await getProductById(product.id);
      if (latestProduct) {
        // Merge latest product data with provided product (provided product takes precedence for user selections)
        productWithImage = { ...latestProduct, ...product };

        // Ensure image data is preserved from latestProduct if missing in provided product
        if (!productWithImage.base64Data && latestProduct.base64Data) {
          productWithImage.base64Data = latestProduct.base64Data;
        }
        if (!productWithImage.imageUrl && latestProduct.imageUrl) {
          productWithImage.imageUrl = latestProduct.imageUrl;
        }

        // Also check selectedVariant for image data
        if (product.selectedVariant && latestProduct.selectedVariant) {
          if (!productWithImage.selectedVariant?.base64Data && latestProduct.selectedVariant.base64Data) {
            if (!productWithImage.selectedVariant) {
              productWithImage.selectedVariant = {};
            }
            productWithImage.selectedVariant.base64Data = latestProduct.selectedVariant.base64Data;
          }
        }

        // Check images array
        if (!productWithImage.base64Data && latestProduct.images && Array.isArray(latestProduct.images) && latestProduct.images.length > 0) {
          const firstImage = latestProduct.images[0];
          if (firstImage && typeof firstImage === 'object' && firstImage.base64Data) {
            productWithImage.base64Data = firstImage.base64Data;
          }
        }
      }
    } catch (err) {
      // If we can't fetch product, use product object passed in
      console.warn('Could not fetch latest product data for wishlist, using provided product data');
    }

    const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);
    const wishlistSnap = await wishlistRef.get();

    let wishlistItems = [];
    if (wishlistSnap.exists) {
      wishlistItems = wishlistSnap.data().items || [];
    }

    const existingItemIndex = wishlistItems.findIndex(item => item.id === product.id);

    // Minimize product data before storing (similar to cart)
    const minimizedProduct = minimizeProductForWishlist({ ...productWithImage });

    if (existingItemIndex >= 0) {
      // Update existing item (preserve base64Data if available)
      wishlistItems[existingItemIndex] = {
        ...wishlistItems[existingItemIndex],
        ...minimizedProduct,
        // Ensure base64Data is explicitly preserved
        base64Data: minimizedProduct.base64Data || wishlistItems[existingItemIndex].base64Data
      };
    } else {
      wishlistItems.push(minimizedProduct);
    }

    // Remove any undefined values from wishlist items before saving
    const cleanedWishlistItems = removeUndefinedValues(wishlistItems);

    // Prepare wishlist document
    const wishlistData = wishlistSnap.exists ? {
      items: cleanedWishlistItems,
      updatedAt: new Date(),
    } : {
      userId,
      items: cleanedWishlistItems,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Compress wishlist document if it exceeds 1MB
    const compressedWishlistData = compressWishlistDocument(wishlistData);

    if (wishlistSnap.exists) {
      await wishlistRef.update(compressedWishlistData);
    } else {
      await wishlistRef.set(compressedWishlistData);
    }

    return compressedWishlistData.items || cleanedWishlistItems;
  } catch (error) {
    console.error("Error adding item to wishlist:", error);
    throw error;
  }
};

export const removeItemFromWishlist = async (userId, productId) => {
  try {
    const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);
    const wishlistSnap = await wishlistRef.get();

    if (!wishlistSnap.exists) {
      return [];
    }

    let wishlistItems = wishlistSnap.data().items || [];
    wishlistItems = wishlistItems.filter(item => item.id !== productId);

    await wishlistRef.update({
      items: wishlistItems,
      updatedAt: new Date(),
    });

    return wishlistItems;
  } catch (error) {
    console.error("Error removing item from wishlist:", error);
    throw error;
  }
};

export const isItemInWishlist = async (userId, productId) => {
  try {
    const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);
    const wishlistSnap = await wishlistRef.get();

    if (!wishlistSnap.exists) {
      return false;
    }

    const wishlistItems = wishlistSnap.data().items || [];
    return wishlistItems.some(item => item.id === productId);
  } catch (error) {
    console.error("Error checking wishlist item:", error);
    return false;
  }
};

// Clear operations
export const clearUserCart = async (userId) => {
  try {
    const cartRef = db.collection(CARTS_COLLECTION).doc(userId);
    await cartRef.update({
      items: [],
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error clearing cart:", error);
    throw error;
  }
};

export const clearUserWishlist = async (userId) => {
  try {
    const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);
    await wishlistRef.update({
      items: [],
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error clearing wishlist:", error);
    throw error;
  }
};

// Subscription functions
export const subscribeToUserCart = (userId, callback) => {
  const cartRef = db.collection(CARTS_COLLECTION).doc(userId);

  return cartRef.onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const cartData = snapshot.data();
      callback(cartData.items || []);
    } else {
      callback([]);
    }
  }, (error) => {
    // Silently handle permission-denied (expected for guests/unauthenticated users)
    if (error.code === 'permission-denied') {
      // Don't log - this is expected for guests
      callback([]);
      return;
    }
    // Only log unexpected errors
    console.warn("Error in cart subscription:", error.code || error.message);
    callback([]);
  });
};

export const subscribeToUserWishlist = (userId, callback) => {
  const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);

  return wishlistRef.onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const wishlistData = snapshot.data();
      callback(wishlistData.items || []);
    } else {
      callback([]);
    }
  }, (error) => {
    // Silently handle permission-denied (expected for guests/unauthenticated users)
    if (error.code === 'permission-denied') {
      // Don't log - this is expected for guests
      callback([]);
      return;
    }
    // Only log unexpected errors
    console.warn("Error in wishlist subscription:", error.code || error.message);
    callback([]);
  });
};

// Statistics functions
export const getCartStats = async (userId) => {
  try {
    const cartRef = db.collection(CARTS_COLLECTION).doc(userId);
    const cartSnap = await cartRef.get();

    if (!cartSnap.exists) {
      return { totalItems: 0, totalValue: 0, itemCount: 0 };
    }

    const cartItems = cartSnap.data().items || [];
    const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);
    const totalValue = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    const itemCount = cartItems.length;

    return { totalItems, totalValue, itemCount };
  } catch (error) {
    console.error("Error getting cart stats:", error);
    return { totalItems: 0, totalValue: 0, itemCount: 0 };
  }
};

export const getWishlistStats = async (userId) => {
  try {
    const wishlistRef = db.collection(WISHLISTS_COLLECTION).doc(userId);
    const wishlistSnap = await wishlistRef.get();

    if (!wishlistSnap.exists) {
      return { totalItems: 0, totalValue: 0 };
    }

    const wishlistItems = wishlistSnap.data().items || [];
    const totalItems = wishlistItems.length;
    const totalValue = wishlistItems.reduce((total, item) => total + item.price, 0);

    return { totalItems, totalValue };
  } catch (error) {
    console.error("Error getting wishlist stats:", error);
    return { totalItems: 0, totalValue: 0 };
  }
};

// Test connection
export const testFirestoreConnection = async () => {
  try {
    const products = await getProducts();
    return { success: true, productCount: products.length };
  } catch (error) {
    console.error("Firestore connection failed:", error);
    return { success: false, error: error.message };
  }
};

// Force offline mode - use cached data
export const forceOfflineMode = () => {
  try {
    // No localStorage - return empty array
    const cachedProducts = [];
    return cachedProducts;
  } catch (error) {
    console.error("Error activating offline mode:", error);
    throw error;
  }
};

// Reset real-time connection
export const resetRealTimeConnection = () => {
  try {
    // Clear any existing listeners
    clearAllListeners();

    // Reset rate limiting
    requestCount = 0;
    lastRequestTime = 0;
  } catch (error) {
    console.error("Error resetting connection:", error);
    throw error;
  }
};

// Clear all listeners
export const clearAllListeners = () => {
  try {
    // Note: In Firebase v8, listeners are typically managed by the components
    // that create them. This function serves as a placeholder for cleanup.
    // Individual components should unsubscribe from their listeners.
  } catch (error) {
    console.error("Error clearing listeners:", error);
    throw error;
  }
};

// Coupon operations
export const getActiveCoupons = async () => {
  try {
    // Ensure Firebase is initialized before using db
    const { db: firestoreDb } = await ensureFirebaseInitialized();
    if (!firestoreDb) {
      console.warn('⚠️ Firestore database not initialized');
      return {};
    }

    const couponsRef = firestoreDb.collection(COUPONS_COLLECTION);
    const snapshot = await couponsRef.where('isActive', '==', true).get();

    const coupons = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      coupons[data.code] = {
        id: doc.id,
        ...data
      };
    });

    return coupons;
  } catch (error) {
    console.error("Error getting active coupons:", error);
    return {};
  }
};

export const getCouponByCode = async (couponCode) => {
  try {
    const couponsRef = db.collection(COUPONS_COLLECTION);
    const snapshot = await couponsRef.where('code', '==', couponCode.toUpperCase()).get();

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }

    return null;
  } catch (error) {
    console.error("Error getting coupon by code:", error);
    return null;
  }
};

export const createCoupon = async (couponData) => {
  try {
    if (!couponData.code || !couponData.discount || !couponData.type) {
      throw new Error("Missing required fields: code, discount, and type are required");
    }

    const couponsRef = db.collection(COUPONS_COLLECTION);
    const docRef = await couponsRef.add({
      ...couponData,
      code: couponData.code.toUpperCase(),
      isActive: couponData.isActive !== undefined ? couponData.isActive : true,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating coupon:", error);
    throw error;
  }
};

export const updateCoupon = async (couponId, updateData) => {
  try {
    const couponRef = db.collection(COUPONS_COLLECTION).doc(couponId);
    await couponRef.update({
      ...updateData,
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error updating coupon:", error);
    throw error;
  }
};

export const deleteCoupon = async (couponId) => {
  try {
    const couponRef = db.collection(COUPONS_COLLECTION).doc(couponId);
    await couponRef.delete();
  } catch (error) {
    console.error("Error deleting coupon:", error);
    throw error;
  }
};

// User profile operations
export const createUserFromAuthData = async (authUser, additionalData = {}) => {
  try {
    const userRef = db.collection('users').doc(authUser.uid);

    const userData = {
      uid: authUser.uid,
      email: authUser.email,
      displayName: authUser.displayName || additionalData.displayName || '',
      phoneNumber: authUser.phoneNumber || additionalData.phoneNumber || '',
      photoURL: authUser.photoURL || additionalData.photoURL || '',
      emailVerified: authUser.emailVerified || false,
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date(),
      status: 'active',
      ...additionalData
    };

    await userRef.set(userData);
    return authUser.uid;
  } catch (error) {
    console.error("Error creating user from auth data:", error);
    throw error;
  }
};

export const updateUserLastLogin = async (userId) => {
  try {
    const userRef = db.collection('users').doc(userId);
    await userRef.update({
      lastLoginAt: new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("Error updating user last login:", error);
    throw error;
  }
};

// Announcement functions
export const getAnnouncements = async () => {
  try {
    const announcementsRef = db.collection('announcements');
    const snapshot = await announcementsRef.orderBy('createdAt', 'desc').get();

    const announcements = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return announcements;
  } catch (error) {
    console.error('Error fetching announcements:', error);
    throw error;
  }
};

// Subscribe to active announcements - Real-time listener
export const subscribeToActiveAnnouncements = (callback) => {
  // Wait for Firebase initialization
  let unsubscribe = null;
  let isSubscribed = false;

  const setupSubscription = async () => {
    try {
      const { db: firestoreDb } = await ensureFirebaseInitialized();
      if (!firestoreDb) {
        console.warn('⚠️ Firestore database not initialized');
        callback([]);
        return;
      }

      if (isSubscribed) return; // Already subscribed

      const announcementsRef = firestoreDb.collection('announcements');
      const query = announcementsRef.where('isActive', '==', true);

      unsubscribe = query.onSnapshot(
        (snapshot) => {
          const now = new Date();
          const allAnnouncements = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Filter by date range
          const announcements = allAnnouncements.filter(announcement => {
            let startDate = null;
            let endDate = null;

            if (announcement.startDate) {
              startDate = announcement.startDate.toDate ? announcement.startDate.toDate() : new Date(announcement.startDate);
            }
            if (announcement.endDate) {
              endDate = announcement.endDate.toDate ? announcement.endDate.toDate() : new Date(announcement.endDate);
            }

            if (!startDate && !endDate) return true;
            if (startDate && now < startDate) return false;
            if (endDate && now > endDate) return false;
            return true;
          });

          // Sort by priority and date
          const sorted = announcements.sort((a, b) => {
            if (a.priority !== b.priority) {
              return (b.priority || 1) - (a.priority || 1);
            }
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateB - dateA;
          });

          callback(sorted);
        },
        (error) => {
          console.error('Error in announcements subscription:', error);
          callback([]);
        }
      );

      isSubscribed = true;
    } catch (error) {
      console.error("Error setting up announcements subscription:", error);
      callback([]);
    }
  };

  // Start subscription setup
  setupSubscription();

  // Return unsubscribe function
  return () => {
    if (unsubscribe) {
      unsubscribe();
      isSubscribed = false;
    }
  };
};

export const createAnnouncement = async (announcementData) => {
  try {
    const announcementsRef = db.collection('announcements');

    const newAnnouncement = {
      ...announcementData,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await announcementsRef.add(newAnnouncement);
    return {
      id: docRef.id,
      ...newAnnouncement
    };
  } catch (error) {
    console.error('Error creating announcement:', error);
    throw error;
  }
};

export const updateAnnouncement = async (announcementId, updateData) => {
  try {
    const announcementRef = db.collection('announcements').doc(announcementId);

    const updatePayload = {
      ...updateData,
      updatedAt: new Date()
    };

    await announcementRef.update(updatePayload);
    return {
      id: announcementId,
      ...updatePayload
    };
  } catch (error) {
    console.error('Error updating announcement:', error);
    throw error;
  }
};

export const deleteAnnouncement = async (announcementId) => {
  try {
    const announcementRef = db.collection('announcements').doc(announcementId);

    await announcementRef.delete();
    return true;
  } catch (error) {
    console.error('Error deleting announcement:', error);
    throw error;
  }
};

export const toggleAnnouncementStatus = async (announcementId, isActive) => {
  try {
    const announcementRef = db.collection('announcements').doc(announcementId);

    await announcementRef.update({
      isActive: isActive,
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    console.error('Error toggling announcement status:', error);
    throw error;
  }
};

// Product subscription - Real-time listener with cache support
export const subscribeToProducts = (callback) => {
  if (!db) {
    console.warn('⚠️ Firestore database not initialized');
    callback([]);
    return () => { };
  }

  const productsRef = db.collection(PRODUCTS_COLLECTION);

  // onSnapshot automatically uses cache first, then syncs with server
  // This provides instant loading from cache + real-time updates
  // In Firebase v8, onSnapshot listens to both cache and server automatically
  let isFirstCall = true;
  const unsubscribe = productsRef.onSnapshot(
    (snapshot) => {
      const products = snapshot.docs.map((doc) => {
        const data = doc.data();
        const { id: customId, ...dataWithoutId } = data;
        return {
          id: doc.id,
          ...dataWithoutId,
          customId: customId || null
        };
      });

      // Log for debugging - helps track when products update
      if (isFirstCall) {
        console.log(`📦 Products subscription initialized: ${products.length} products`);
        isFirstCall = false;
      } else {
        console.log(`✅ Products subscription updated: ${products.length} products (real-time update)`);
      }

      // Always call callback with latest products - this ensures UI updates
      callback(products);
    },
    (error) => {
      console.error("❌ Error in products subscription:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Return empty array on error, but don't crash
      callback([]);
    }
  );

  return unsubscribe;
};

// Subscribe to products by category - Real-time listener
// Uses fallback approach: try exact match first, then filter client-side if needed
export const subscribeToProductsByCategory = (category, callback) => {
  if (!db) {
    console.warn('⚠️ Firestore database not initialized');
    callback([]);
    return () => { };
  }

  if (!category) {
    callback([]);
    return () => { };
  }

  const normalizedCategory = String(category).toLowerCase().trim();
  const productsRef = db.collection(PRODUCTS_COLLECTION);

  // Try exact match query first
  const query = productsRef.where("category", "==", normalizedCategory);

  let isFirstCall = true;
  let unsubscribeMain = null;
  let unsubscribeAll = null;

  // First, try the exact query
  unsubscribeMain = query.onSnapshot(
    (snapshot) => {
      const products = snapshot.docs.map((doc) => {
        const data = doc.data();
        const { id: customId, ...dataWithoutId } = data;
        return {
          id: doc.id,
          ...dataWithoutId,
          customId: customId || null
        };
      });

      // If we got products, use them
      if (products.length > 0) {
        if (isFirstCall) {
          console.log(`📦 Category products subscription initialized: ${products.length} products for "${category}"`);
          isFirstCall = false;
        } else {
          console.log(`✅ Category products subscription updated: ${products.length} products for "${category}" (real-time update)`);
        }
        callback(products);
        return;
      }

      // If no products from exact match, subscribe to all products and filter client-side
      // This handles case variations and category name mismatches
      if (!unsubscribeAll) {
        console.log(`⚠️ No exact match for "${normalizedCategory}", subscribing to all products and filtering client-side`);
        unsubscribeAll = productsRef.onSnapshot(
          (allSnapshot) => {
            const allProducts = allSnapshot.docs.map((doc) => {
              const data = doc.data();
              const { id: customId, ...dataWithoutId } = data;
              return {
                id: doc.id,
                ...dataWithoutId,
                customId: customId || null
              };
            });

            // Debug: Log all unique categories found in products
            const uniqueCategories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
            console.log(`🔍 All product categories found:`, uniqueCategories);
            console.log(`🔍 Looking for category: "${normalizedCategory}"`);

            // Filter products by category (case-insensitive, handle variations)
            const filtered = allProducts.filter(product => {
              if (!product.category) return false;
              const productCat = String(product.category).toLowerCase().trim();

              // Exact match
              if (productCat === normalizedCategory) return true;

              // Handle common variations
              const variations = [
                normalizedCategory,
                normalizedCategory.replace(/-/g, '_'),
                normalizedCategory.replace(/_/g, '-'),
                normalizedCategory.replace(/\s+/g, '-'),
                normalizedCategory.replace(/-/g, ' ')
              ];

              return variations.some(variation => {
                const productVariations = [
                  productCat,
                  productCat.replace(/-/g, '_'),
                  productCat.replace(/_/g, '-'),
                  productCat.replace(/\s+/g, '-'),
                  productCat.replace(/-/g, ' ')
                ];
                return productVariations.some(pv => variations.includes(pv));
              });
            });

            if (isFirstCall) {
              console.log(`📦 Category products (filtered): ${filtered.length} products for "${category}"`);
              if (filtered.length === 0 && allProducts.length > 0) {
                console.warn(`⚠️ No products matched category "${normalizedCategory}". Available categories:`, uniqueCategories);
              }
              isFirstCall = false;
            } else {
              console.log(`✅ Category products (filtered) updated: ${filtered.length} products for "${category}"`);
            }

            callback(filtered);
          },
          (error) => {
            console.error("❌ Error in all products subscription (fallback):", error);
            callback([]);
          }
        );
      }
    },
    (error) => {
      // If query fails (e.g., missing index), fall back to all products
      console.warn(`⚠️ Category query failed for "${normalizedCategory}", using fallback:`, error.message);

      if (!unsubscribeAll) {
        unsubscribeAll = productsRef.onSnapshot(
          (allSnapshot) => {
            const allProducts = allSnapshot.docs.map((doc) => {
              const data = doc.data();
              const { id: customId, ...dataWithoutId } = data;
              return {
                id: doc.id,
                ...dataWithoutId,
                customId: customId || null
              };
            });

            // Filter client-side
            const filtered = allProducts.filter(product => {
              if (!product.category) return false;
              const productCat = String(product.category).toLowerCase().trim();
              return productCat === normalizedCategory ||
                productCat.replace(/-/g, '_') === normalizedCategory.replace(/-/g, '_');
            });

            console.log(`📦 Category products (fallback filtered): ${filtered.length} products for "${category}"`);
            callback(filtered);
          },
          (err) => {
            console.error("❌ Error in fallback subscription:", err);
            callback([]);
          }
        );
      }
    }
  );

  // Return cleanup function
  return () => {
    if (unsubscribeMain) unsubscribeMain();
    if (unsubscribeAll) unsubscribeAll();
  };
};

/**
 * Migration helper: Fix existing cart items missing image data
 * Run this once to update all cart items in Firestore with proper image data
 * 
 * Usage: Call this function from browser console or admin panel
 * await migrateCartItemsImages();
 */
export const migrateCartItemsImages = async () => {
  try {
    console.log('🔄 Starting cart items image migration...');
    const cartsRef = db.collection(CARTS_COLLECTION);
    const cartsSnapshot = await cartsRef.get();

    let totalCarts = 0;
    const counters = {
      totalItemsFixed: 0,
      totalItemsSkipped: 0
    };

    for (const cartDoc of cartsSnapshot.docs) {
      const cartData = cartDoc.data();
      const items = cartData.items || [];

      if (items.length === 0) {
        continue;
      }

      totalCarts++;
      let cartUpdated = false;
      const updatedItems = await Promise.all(
        items.map(async (item) => {
          // Skip if item already has base64Data or valid imageUrl
          if (item.base64Data || (item.imageUrl && !item.imageUrl.startsWith('blob:'))) {
            counters.totalItemsSkipped++;
            return item;
          }

          // Try to fetch image from product
          try {
            const product = await getProductById(item.id);
            if (product) {
              // Extract base64Data using same logic as minimizeProductForCart
              let base64Data = null;
              let imageUrl = null;

              // Priority: product.base64Data → images[0].base64Data → product.imageUrl
              if (product.base64Data) {
                base64Data = product.base64Data;
              } else if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                const firstImage = product.images[0];
                if (firstImage && typeof firstImage === 'object' && firstImage.base64Data) {
                  base64Data = firstImage.base64Data;
                } else if (typeof firstImage === 'string' && firstImage.startsWith('data:image')) {
                  base64Data = firstImage;
                } else if (typeof firstImage === 'object' && firstImage.imageUrl) {
                  imageUrl = firstImage.imageUrl;
                } else if (typeof firstImage === 'string') {
                  imageUrl = firstImage;
                }
              } else if (product.imageUrl && !product.imageUrl.startsWith('blob:')) {
                imageUrl = product.imageUrl;
              }

              if (base64Data || imageUrl) {
                const updatedItem = { ...item };
                if (base64Data) {
                  updatedItem.base64Data = base64Data;
                }
                if (imageUrl && !updatedItem.imageUrl) {
                  updatedItem.imageUrl = imageUrl;
                }
                counters.totalItemsFixed++;
                cartUpdated = true;
                return updatedItem;
              }
            }
          } catch (error) {
            console.warn(`Could not fetch product ${item.id} for migration:`, error);
          }

          return item;
        })
      );

      // Update cart if any items were fixed
      if (cartUpdated) {
        await cartDoc.ref.update({
          items: updatedItems,
          updatedAt: new Date()
        });
        console.log(`✅ Updated cart ${cartDoc.id} with ${updatedItems.filter((item, idx) => item !== items[idx]).length} fixed items`);
      }
    }

    console.log(`✅ Migration complete!`);
    console.log(`   - Carts processed: ${totalCarts}`);
    console.log(`   - Items fixed: ${counters.totalItemsFixed}`);
    console.log(`   - Items skipped (already had images): ${counters.totalItemsSkipped}`);

    return {
      success: true,
      cartsProcessed: totalCarts,
      itemsFixed: counters.totalItemsFixed,
      itemsSkipped: counters.totalItemsSkipped
    };
  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
};
